/* eslint-disable import/no-cycle */
import moment from 'moment';
import { compact, get, groupBy, uniq, keyBy, isEmpty } from 'lodash';
import {
  camelToSnake,
  convertObjToCamelCase,
  convertObjToSnakeCase,
  convertPascalToSnake,
  retrieveListOrObject,
  retrieveObjectMapper,
  retrieveSortForMS,
} from 'utils';
import DatadogHandler from 'utils/datadog';
import { transformBankBicCode } from 'utils/ms-utils/ms-phase-3/BankAccountUtils';
import {
  getChildByIds,
  getDocumentTypesByBankAccountIds,
} from 'services/ChildService';
import {
  getInvoicesByIds,
  msGetInvoiceItemByIds,
  msGetCreditDebitNotesByInvoiceIds,
} from 'services/InvoiceService';
import {
  getBankBicCodeByIds,
  getBankAccountStateHistoryByAccountIds,
  getAllBankBicCode,
} from 'services/BankAccountService';

import {
  getRegistrationChildMappingByRegistrationChildIds,
  getRegistrationChildByIds,
  getEcdaRegistrationChildByIds,
} from 'services/RegistrationsService';

import {
  childService,
  financeService,
  MS_PHASE_3_ENDPOINTS,
  onboardingService,
} from 'utils/msEndpointConstants';
import { transformApplicableEnquiryDiscounts } from 'utils/ms-utils/ms-phase-3/DiscountUtils';
import {
  DEFAULT_ERROR,
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_PAGE,
  MAX_PER_PAGE,
  DATE_PICKER_FORMAT,
} from '../utils/constants';
import {
  transformCreditDebitNotes,
  transformFetchAccountInfoByECDAReg,
  transformAllInvoices,
  transformGetECDARegistrationRefundChildBalance,
  transformAdvancePaymentReceipts,
  transformAdvancePaymentReceiptsForRegistration,
  transformRefundList,
  transformEcdaRegistrationInvoice,
  transformChildSubsidyHistories,
  transformFindAllValidDownloadedGiroFiles,
} from '../utils/ms-utils/ms-phase-3/FinanceUtils';
import { getUserByIds } from './UserService';
import { retrieveDataMapper } from '../utils';
import { getDiscountItemGroupByIds } from './DiscountService';
import { getCentreByIds } from './CentreService';

const fileName = 'src/services/FinanceService.js';
const {
  FINANCE,
  INVOICE,
  REFUND,
  RECEIPT,
  CHILD,
  ECDA_REGISTRATIONS,
  REGISTRATIONS,
} = MS_PHASE_3_ENDPOINTS;

export const msGetAllBankBicCodes = async (_, reqBody) => {
  try {
    const params = {
      ...retrieveSortForMS(get(reqBody, 'pagination.sort'), 'customSort'),
    };
    const resp = await getAllBankBicCode(params);
    return {
      success: true,
      data: get(resp, 'data', [])?.map(data => transformBankBicCode({ data })),
      totalCount: get(resp, 'totalCount', 0),
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAllBankBicCodes',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msRemoveChildDiscountForRegistration = async (_, reqBody) => {
  try {
    const childID = get(reqBody, 'registrationChildID');
    const discountArrangementId = get(reqBody, 'discountArrangementId');
    const remarks = get(reqBody, 'remarks');

    const response = await financeService().post(
      ...FINANCE.removeRegistrationChildDiscount({
        child_id: childID,
        discount_arrangement_id: discountArrangementId,
        remarks,
      })
    );

    const responseData = get(response, 'data', {});

    return {
      data: {
        removeDiscountFromRegistrationChild: get(responseData, 'success', true),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msRemoveChildDiscountForRegistration',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msDeactivateChildDiscountForRegistration = async (_, reqBody) => {
  try {
    const registrationChildID = get(reqBody, 'registrationChildID');
    const discountArrangementId = get(reqBody, 'discountArrangementId');
    const remarks = get(reqBody, 'remarks');

    const response = await financeService().post(
      ...FINANCE.deactivateRegistrationChildDiscount({
        child_id: registrationChildID,
        discount_arrangement_id: discountArrangementId,
        remarks,
      })
    );

    const responseData = get(response, 'data', {});

    return {
      data: {
        deactivateRegistrationChildDiscount: get(responseData, 'success', true),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msDeactivateChildDiscountForRegistration',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msDeactivateChildDiscountForEcdaRegistration = async (
  _,
  reqBody
) => {
  try {
    const registrationChildID = get(reqBody, 'registrationChildID');
    const discountArrangementId = get(reqBody, 'discountArrangementId');
    const remarks = get(reqBody, 'remarks');

    const response = await financeService().post(
      ...FINANCE.deactivateEcdaRegistrationChildDiscount({
        child_id: registrationChildID,
        discount_arrangement_id: discountArrangementId,
        remarks,
      })
    );

    const responseData = get(response, 'data', {});

    return {
      data: {
        deactivateEcdaRegistrationChildDiscount: get(
          responseData,
          'success',
          true
        ),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msDeactivateChildDiscountForEcdaRegistration',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};
export const msFetchAdvancePaymentReceiptsForRegistration = async (
  _,
  reqData
) => {
  try {
    const financeSvc = financeService();
    const params = {
      page: get(reqData, 'pagination.page'),
      perPage: get(reqData, 'pagination.perPage'),
      ...retrieveSortForMS(get(reqData, 'pagination.sort'), 'sort'),
    };
    const resp = await financeSvc.post(
      ...FINANCE.getAdvancePaymentReceiptsForRegistration(
        {
          registration_child_id: get(reqData, 'registrationChildID'),
          registration_id: get(reqData, 'registrationID'),
        },
        params
      )
    );
    const transformedData = transformAdvancePaymentReceiptsForRegistration(
      get(resp, 'data.data', [])
    );
    return {
      data: {
        getAdvancePaymentReceiptsForRegistration: {
          data: transformedData,
        },
        totalCount: get(resp, 'data.totalCount'),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msmonoFetchAdvancePaymentReceiptsForRegistration',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchAllPaymentTypes = async () => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.get(FINANCE.getOfflinePaymentModes);

    const modes = get(resp, 'data.data', []);
    const totalCount = get(
      resp,
      'data.totalCount',
      Array.isArray(modes) ? modes.length : 0
    );
    const data = (Array.isArray(modes) ? modes : []).map(item => ({
      label: get(item, 'label', ''),
      value: get(item, 'value', ''),
    }));

    return {
      data: {
        getOfflinePaymentModes: {
          data,
          totalCount,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchAllPaymentTypes',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error:
        get(ex, 'response.data.error') || get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCreateAdvancePaymentReceiptForRegistration = async (
  _,
  reqData
) => {
  try {
    const reqBody = {
      amount: reqData?.amount || 0,
      ...(reqData?.registrationChildID && {
        registration_child_id: reqData?.registrationChildID,
      }),
      ...(reqData?.registrationID && {
        registration_id: reqData?.registrationID,
      }),
      ...(reqData?.isSiblingCda && { is_sibling_cda: reqData?.isSiblingCda }),
      ...(reqData?.paymentType && { payment_method: reqData?.paymentType }),
      ...(reqData?.referenceNo && { reference_no: reqData?.referenceNo }),
      ...(reqData?.remarks && { remarks: reqData?.remarks }),
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...RECEIPT.createAdvancePaymentReceiptForRegistration(reqBody)
    );
    const receipt = resp?.data?.receipt;
    const data =
      receipt &&
      retrieveObjectMapper(receipt, [
        'ID:id',
        'receipt_no',
        'amount',
        'amount_received',
        'credit_amount_used',
        'created_at',
        'paymentType:payment_type_string',
        'pdfUrl:pdf_url_key',
        'preSignedUrl',
        'document_no',
        'cancelation_reason',
        'status',
        'remarks',
        'cancelled_date',
        'unlinked_reason',
        'type',
      ]);
    return {
      data: {
        createAdvancePaymentReceiptForRegistration: data || {},
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCreateAdvancePaymentReceiptForRegistration',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCancelAdvancePaymentReceipt = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.cancelAdvancePaymentReceipt({
        receipt_id: get(reqData, 'receipt_id'),
        remark: get(reqData, 'remark'),
      })
    );

    return {
      success: true,
      data: {
        cancelAdvancePaymentReceipt: get(resp, 'data', {}),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCancelAdvancePaymentReceipt',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetRegistrationChildBalanceSheet = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.getRegistrationChildBalanceSheet({
        childId: get(reqData, 'registrationChildID'),
        centreId: get(reqData, 'IDCentre'),
      })
    );

    return {
      success: true,
      data: {
        getRegistrationChildBalanceSheet: retrieveObjectMapper(
          get(resp, 'data', {}),
          [
            'cda_credit',
            'non_cda_credit',
            'cda_outstanding',
            'non_cda_outstanding',
          ]
        ),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetRegistrationChildBalanceSheet',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const getReceiptByInvoiceIds = async (invoiceIds = []) => {
  try {
    if (invoiceIds?.length === 0) {
      return {
        success: true,
        data: {},
      };
    }
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.getReceiptByInvoiceIds(invoiceIds)
    );
    return {
      success: true,
      data: resp?.data?.result || {},
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'getReceiptByInvoiceIds',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFindAllInvoiceForAllCenter = async (_, reqData) => {
  try {
    const { pagination } = reqData;
    const params = {
      page: pagination?.page || 1,
      perPage: pagination?.perPage || 10,
      ...retrieveSortForMS(pagination?.sort || []),
    };
    const filter = {
      ...(reqData?.filter?.ID && {
        id: [{ eq: reqData?.filter?.ID }],
      }),
      ...(reqData?.filter?.fkCentre && {
        fk_centre: [{ eq: reqData?.filter?.fkCentre }],
      }),
      ...(reqData?.filter?.fkEcdaRegistration && {
        fk_ecda_registration: [{ eq: reqData?.filter?.fkEcdaRegistration }],
      }),
      ...(reqData?.filter?.fkEcdaRegistrationChild && {
        fk_ecda_registration_child: [
          { eq: reqData?.filter?.fkEcdaRegistrationChild },
        ],
      }),
      ...(reqData?.filter?.status && {
        status: reqData?.filter?.status,
      }),
      ...(reqData?.filter?.createdAt && {
        created_at: reqData?.filter?.createdAt,
      }),
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...INVOICE.findAllInvoice({ filter, params })
    );
    const invoiceData = resp?.data?.data || [];
    const invoiceIds = compact(uniq(invoiceData?.map(item => item?.id)));
    const fkChildIds = compact(uniq(invoiceData?.map(item => item?.fk_child)));
    const fkEcdaRegistrationChildIds = compact(
      uniq(invoiceData?.map(item => item?.fk_ecda_registration_child))
    );
    const fkRegistrationChildIds = compact(
      uniq(invoiceData?.map(item => item?.fk_registration_child))
    );
    if (invoiceIds?.length === 0) {
      return {
        success: true,
        data: {
          findAllInvoice: {
            totalCount: 0,
            data: [],
          },
        },
      };
    }

    const [ecdaRegistrationChild, registrationChildMapping, registrationChild] =
      await Promise.all([
        fkEcdaRegistrationChildIds?.length > 0
          ? getEcdaRegistrationChildByIds(fkEcdaRegistrationChildIds)
          : {},
        fkRegistrationChildIds?.length > 0
          ? getRegistrationChildMappingByRegistrationChildIds(
              fkRegistrationChildIds
            )
          : {},
        fkRegistrationChildIds?.length > 0
          ? getRegistrationChildByIds(fkRegistrationChildIds)
          : {},
      ]);

    const fkChildByEcdaRegistrations = Object.values(ecdaRegistrationChild).map(
      item => item?.fk_child
    );
    const fkChildByRegistrations = Object.values(registrationChild).map(
      item => item?.fk_child
    );

    const [allInvoices, creditDebitNotes, childData, receiptData] =
      await Promise.all([
        msGetInvoiceItemByIds(null, { invoice_ids: invoiceIds }),
        msGetCreditDebitNotesByInvoiceIds(null, { invoiceIds }),
        getChildByIds(
          uniq(
            compact([
              ...fkChildIds,
              ...fkChildByEcdaRegistrations,
              ...fkChildByRegistrations,
            ])
          )
        ),
        getReceiptByInvoiceIds(invoiceIds),
      ]);
    const invoiceItems = allInvoices?.data?.invoiceItems?.data || [];
    const child = childData?.map(ch => ({
      ID: ch?.id,
      firstname: ch?.firstname,
      lastname: ch?.lastname,
    }));

    // we don't really need invoiceVoidNotes field as there is no data in monolith response as well.
    const data = transformAllInvoices({
      invoices: invoiceData,
      creditDebitNotes: creditDebitNotes?.data || {},
      invoiceItems: groupBy(invoiceItems, 'fkInvoice'),
      receipts: receiptData?.data || {},
      ecdaRegistrationChild,
      registrationChild,
      registrationChildMapping,
      child,
    });

    return {
      success: true,
      data: {
        findAllInvoice: {
          totalCount: resp?.data?.totalCount || 0,
          data,
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFindAllInvoiceForAllCenter',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetRegistrationRefundChildBalance = async (_, reqData) => {
  try {
    if (!reqData?.registrationChildID) {
      return {
        data: {
          getRegistrationRefundChildBalance: {
            data: [],
            totalCount: 0,
          },
        },
        success: true,
      };
    }
    const { pagination } = reqData;
    const reqBody = {
      ...(reqData?.registrationChildID && {
        registration_child_id: reqData?.registrationChildID,
      }),
      ...(reqData?.registrationID && {
        registration_id: reqData?.registrationID,
      }),
      ...(reqData?.fts && {
        fts: reqData?.fts,
      }),
    };
    const params = {
      ...(pagination?.page && {
        page: pagination?.page || DEFAULT_PAGE,
      }),
      ...(pagination?.perPage && {
        perPage: pagination?.perPage || DEFAULT_ITEMS_PER_PAGE,
      }),
      ...(pagination?.sort && retrieveSortForMS(pagination?.sort || [])),
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...REFUND.getRegRefundChildBalance(reqBody, params)
    );
    return {
      data: {
        getRegistrationRefundChildBalance: {
          data:
            resp?.data?.data?.map(item => ({
              ...retrieveObjectMapper(item, [
                'ID:id',
                'refund_no',
                'refund_at',
                'amount',
                'requested_at',
                'refundMode:refund_mode_string',
                'status:status_string',
                'cancellation_reason',
                'remark',
                'type',
              ]),
            })) || [],
          totalCount: resp?.data?.totalCount || 0,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetRegistrationRefundChildBalance',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const getCreditDebitNoteItems = (creditDebitNoteIds = []) => {
  if (creditDebitNoteIds.length === 0) return Promise.resolve([]);
  return financeService()
    .post(...FINANCE.getCreditDebitNoteItems(creditDebitNoteIds))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const getFeeDraftItems = (filter, params) => {
  return financeService()
    .post(FINANCE.getFeeDraftItems, filter, params)
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const getFeeGroups = filter => {
  return financeService()
    .post(FINANCE.getFeeGroups, filter)
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const getApprovedFeeDraftByFeeTierIds = feeTierIds => {
  return financeService()
    .post(FINANCE.getApprovedFeeDraftByFeeTierIds, { fee_tier_ids: feeTierIds })
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const getFeeByFeeTierIds = (feeTierIds, pagination) => {
  return financeService()
    .post(
      FINANCE.getFeeByFeeTierIds,
      {
        fee_tier_ids: feeTierIds,
      },
      {
        params: {
          page: pagination?.page || 1,
          perPage: pagination?.perPage || MAX_PER_PAGE,
        },
      }
    )
    .then(res => get(res, 'data', []))
    .catch(() => []);
};

export const msFetchAllCreditDebitNotes = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc
      .post(
        FINANCE.getAllCreditDebitNotes,
        {
          write_off: get(reqData, 'writeOff', false),
          linked_invoice_ids: get(reqData, 'linkedInvoiceIds', []),
          ...(get(reqData, 'filter') && {
            filter: {
              ...(get(reqData, 'filter.fkCentre') && {
                fk_centre: [{ eq: get(reqData, 'filter.fkCentre') }],
              }),
              ...(get(reqData, 'filter.createdAt') && {
                created_at: get(reqData, 'filter.createdAt'),
              }),
              ...(get(reqData, 'filter.fkEcdaRegistration') && {
                fk_ecda_registration: [
                  { eq: get(reqData, 'filter.fkEcdaRegistration') },
                ],
              }),
              ...(get(reqData, 'filter.fkEcdaRegistrationChild') && {
                fk_ecda_registration_child: [
                  { eq: get(reqData, 'filter.fkEcdaRegistrationChild') },
                ],
              }),
              ...(get(reqData, 'filter.fkRegistration') && {
                fk_registration: [
                  { eq: get(reqData, 'filter.fkRegistration') },
                ],
              }),
              ...(get(reqData, 'filter.fkRegistrationChild') && {
                fk_registration_child: [
                  { eq: get(reqData, 'filter.fkRegistrationChild') },
                ],
              }),
            },
          }),
        },
        {
          params: {
            page: get(reqData, 'pagination.page', 1),
            perPage: get(reqData, 'pagination.perPage', 10),
            ...retrieveSortForMS(get(reqData, 'pagination.sort', [])),
          },
        }
      )
      .catch(() => ({ data: [], totalCount: 0 }));

    const resData = get(resp, 'data.data', []);
    const creditDebitNoteIds = resData.map(item => item.id);
    const childIds = [...new Set(resData.map(item => item.fk_child))].filter(
      Boolean
    );
    const registrationChildIds = [
      ...new Set(resData.map(item => item.fk_registration_child)),
    ].filter(Boolean);
    const ecdaRegistrationChildIds = [
      ...new Set(resData.map(item => item.fk_ecda_registration_child)),
    ].filter(Boolean);

    const promiseResponses = await Promise.all([
      getCreditDebitNoteItems(creditDebitNoteIds),
      getEcdaRegistrationChildByIds(ecdaRegistrationChildIds),
      getRegistrationChildMappingByRegistrationChildIds(registrationChildIds),
      getRegistrationChildByIds(registrationChildIds),
    ]);

    const creditDebitNoteItems = get(promiseResponses, '[0]', []);
    const ecdaRegistrationChildren = get(promiseResponses, '[1]', []);
    const registrationChildren = get(promiseResponses, '[3]', []);
    const childIdByEcdaRegs = Object.values(ecdaRegistrationChildren)?.map(
      ch => ch?.fk_child
    );
    const childIdByRegs = Object.values(registrationChildren)?.map(
      ch => ch?.fk_child
    );

    const invoiceIds = [
      ...new Set(creditDebitNoteItems.map(item => item.fk_invoice)),
    ];

    const [invoices, children] = await Promise.all([
      getInvoicesByIds(invoiceIds),
      getChildByIds(
        uniq(compact([...childIds, ...childIdByEcdaRegs, ...childIdByRegs]))
      ),
    ]);

    return transformCreditDebitNotes(
      ...[resp, ...promiseResponses, invoices, children]
    );
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchAllCreditDebitNotes',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msTransferRegistrationChildBalance = async (_, reqData = {}) => {
  try {
    const financeSvc = financeService();

    const resp = await financeSvc.post(
      ...FINANCE.transferRegistrationChildBalance(reqData)
    );

    const isSuccess = get(resp, 'data.transferEcdaRegistrationChildBalance');

    return {
      success: isSuccess,
      data: {
        transferRegistrationChildBalance: isSuccess,
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msTransferRegistrationChildBalance',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [{ message: get(ex, 'message', DEFAULT_ERROR) }],
    };
  }
};

export const msTransferChildBalance = async (_, reqData = {}) => {
  try {
    const financeSvc = financeService();
    const reqBody = convertObjToSnakeCase(reqData, [
      'id_child:IDChild',
      'id_centre:IDCentre',
      'from_bucket:FromBucket',
      'to_bucket:ToBucket',
      'remarks:Remarks',
      'amount:Amount',
    ]);

    const resp = await financeSvc.post(
      ...FINANCE.transferChildBalance(reqBody)
    );

    const isSuccess = get(resp, 'data.transferChildBalance');
    const successFlag =
      typeof isSuccess === 'boolean'
        ? isSuccess
        : get(resp, 'data.success', false) || !!get(resp, 'data', false);

    return {
      data: {
        transferChildBalance: Boolean(successFlag),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msTransferChildBalance',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error:
        get(ex, 'response.data.error') || get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msTransferEcdaRegistrationChildBalance = async (
  _,
  reqData = {}
) => {
  try {
    const financeSvc = financeService();
    const reqBody = convertObjToSnakeCase(reqData, [
      'registration_id:IDRegistration',
      'registration_child_id:IDRegistrationChild',
      'centre_id:IDCentre',
      'from_bucket:FromBucket',
      'to_bucket:ToBucket',
      'remarks:Remarks',
      'amount:Amount',
    ]);

    const resp = await financeSvc.post(
      ...FINANCE.transferEcdaRegistrationChildBalance(reqBody)
    );

    const isSuccess = get(resp, 'data.transferEcdaRegistrationChildBalance');
    const successFlag =
      typeof isSuccess === 'boolean'
        ? isSuccess
        : get(resp, 'data.success', false) || !!get(resp, 'data', false);

    return {
      data: {
        transferEcdaRegistrationChildBalance: Boolean(successFlag),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msTransferEcdaRegistrationChildBalance',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error:
        get(ex, 'response.data.error') || get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msInitiateRefundForCancelledRegistration = async (_, reqData) => {
  try {
    const reqBody = {
      is_cda: reqData?.isCDA || false,
      is_non_cda: reqData?.isNonCDA || false,
      registration_id: reqData?.IDRegistration,
      remarks: reqData?.remarks,
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.initiateRefundForCancelledRegistration(reqBody)
    );
    return {
      data: {
        initiateRefundForCancelledRegistration: resp?.data?.initiated || false,
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msInitiateRefundForCancelledRegistration',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSaveRegGiroApplication = async (_, reqData) => {
  try {
    const body = {
      registration_child_id: reqData?.IDRegChild,
      school_id: reqData?.IDSchool,
      is_sibling_cda: reqData?.isSiblingCda || false,
      giro_application: reqData?.giroApplication
        ? convertPascalToSnake(reqData.giroApplication)
        : null,
    };
    const resp = await financeService().post(
      ...FINANCE.saveRegGiroApplication(body)
    );
    return {
      data: {
        saveRegGiroApplication: resp?.data && convertObjToCamelCase(resp?.data),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSaveRegGiroApplication',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSaveGiroApplication = async (_, reqData) => {
  try {
    const body = {
      child_id: reqData?.IDChild,
      school_id: reqData?.IDSchool,
      is_sibling_cda: reqData?.isSiblingCda || false,
      giro_application: reqData?.giroApplication
        ? convertPascalToSnake(reqData.giroApplication)
        : null,
    };
    const resp = await financeService().post(
      ...FINANCE.saveGiroApplication(body)
    );
    return {
      data: {
        saveGiroApplication: resp?.data && convertObjToCamelCase(resp?.data),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSaveGiroApplication',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetGiroFormUploadURL = async (_, reqData) => {
  try {
    if (!reqData?.file) return { success: true, data: {} };

    const body = {
      ...(reqData?.IDChild && {
        child_id: reqData?.IDChild,
      }),
      ...(reqData?.IDEnquiry && {
        enquiry_id: reqData?.IDEnquiry,
      }),
      ...(reqData?.IDEcdaRegChild && {
        reg_child_id: reqData?.IDEcdaRegChild,
      }),
      ...(reqData?.IDBankAccount && {
        bank_account_id: reqData?.IDBankAccount,
      }),
      ...(reqData?.IDDocumentTypeConfig && {
        id_document_type_config: reqData?.IDDocumentTypeConfig,
      }),
      file: convertObjToSnakeCase(reqData?.file),
    };

    const resp = await childService().post(
      ...CHILD.getGetGiroFormUploadURL(body)
    );

    return {
      data: {
        GetGiroFormUploadURL: {
          ...(resp?.data &&
            retrieveObjectMapper(resp?.data, [
              'Url:url',
              'Key:key',
              'Filename:file_name',
            ])),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetGiroFormUploadURL',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetSiblingBirthCertificateUploadURL = async (_, reqData) => {
  try {
    const body = {
      ...(reqData?.IDEnquiry && {
        enquiry_id: reqData?.IDEnquiry,
      }),
      ...(reqData?.IDRegChild && {
        reg_child_id: reqData?.IDRegChild,
      }),
      ...(reqData?.IDDocumentTypeConfig && {
        id_document_type_config: reqData?.IDDocumentTypeConfig,
      }),
      file: reqData?.file && convertObjToSnakeCase(reqData?.file),
      bank_account_id: reqData?.IDBankAccount,
      child_id: reqData?.IDChild,
    };
    const childSvc = childService();
    const resp = await childSvc.post(
      ...CHILD.getSiblingBirthCertificateUploadURL(body)
    );

    return {
      data: {
        getSiblingBirthCertificateUploadURL: {
          ...(resp?.data &&
            retrieveObjectMapper(resp?.data, [
              'Url:url',
              'Key:key',
              'Filename:file_name',
            ])),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetSiblingBirthCertificateUploadURL',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetCdaAccountStatementUploadURL = async (_, reqData) => {
  try {
    const resp = await childService().post(
      CHILD.getCdaAccountStatementUploadURL,
      {
        bank_account_id: get(reqData, 'IDBankAccount'),
        child_id: get(reqData, 'IDChild'),
        enquiry_id: get(reqData, 'IDEnquiry'),
        file: convertObjToSnakeCase(get(reqData, 'file')),
        id_document_type_config: get(reqData, 'IDDocumentTypeConfig'),
        reg_child_id: get(reqData, 'IDRegChild'),
      }
    );

    return {
      data: {
        getCdaAccountStatementUploadURL: {
          Filename: get(resp, 'data.file_name'),
          FileSize: get(resp, 'data.file_size'),
          Key: get(resp, 'data.key'),
          Url: get(resp, 'data.url'),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetCdaAccountStatementUploadURL',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSkipGiroForRegistration = async (_, reqData) => {
  try {
    const { IDRegistration } = reqData;
    const onboardingSvc = onboardingService();
    await onboardingSvc.put(
      REGISTRATIONS.skipGiroForRegistration(IDRegistration)
    );
    return {
      success: true,
      data: {
        skipGiroForRegistration: true,
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSkipGiroForRegistration',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'response.data.error', DEFAULT_ERROR),
    };
  }
};

export const msSkipRegistrationPaymentStep = async () => {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          data: {
            skipRegistrationPaymentStep: true,
          },
          success: true,
        });
      }, 1000);
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSkipRegistrationPaymentStep',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetChildBalanceSheet = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.getChildBalanceSheet({
        childId: get(reqData, 'childID'),
        centreId: get(reqData, 'centreID'),
      })
    );

    return {
      success: true,
      data: {
        getChildBalanceSheet: retrieveObjectMapper(get(resp, 'data', {}), [
          'cda_credit',
          'non_cda_credit',
          'cda_outstanding',
          'non_cda_outstanding',
        ]),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetChildBalanceSheet',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCompleteGiroSetupECDA = async (_, reqData) => {
  try {
    if (!reqData?.IDEcdaRegistration) return { success: true, data: {} };

    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.post(
      ECDA_REGISTRATIONS.markGiroAsCompleted(reqData?.IDEcdaRegistration)
    );

    return {
      data: {
        markGiroAsCompletedForEcdaRegistration: resp?.data?.success || false,
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCompleteGiroSetupECDA',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUpdateChildTradeDebtNotificationConfig = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.updateChildTradeDebtNotification(reqData)
    );

    return {
      success: resp?.data?.success || false,
      data: {
        updateChildTradeDebtNotificationConfig: resp?.data?.success || false,
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUpdateChildTradeDebtNotificationConfig',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetTradeDebtNotifications = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const id = get(reqData, 'childID') || get(reqData, 'id');
    const resp = await financeSvc.get(FINANCE.getTradeDebtNotifications(id));
    const responseData = get(resp, 'data', []);
    const notifications = Array.isArray(responseData)
      ? responseData
      : get(responseData, 'data', []);

    return {
      success: true,
      data: {
        getTradeDebtNotifications: notifications.map(n =>
          convertObjToCamelCase(n, [
            'emailURL:email_url',
            'preSignedURL:pre_signed_url',
          ])
        ),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetTradeDebtNotifications',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetCurrentChildRefundStatus = async () => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.get(FINANCE.getCurrentChildRefundStatus);

    const responseData = get(resp, 'data', {});

    return {
      success: true,
      data: {
        getCurrentChildRefundStatus: convertObjToCamelCase(responseData),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetCurrentChildRefundStatus',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCreateECDAAdvancePaymentReceiptForRegistration = async (
  _,
  reqData
) => {
  try {
    const reqBody = {
      ...(reqData?.amount && {
        amount: reqData?.amount,
      }),
      ...(reqData?.registrationChildID && {
        ecda_registration_child_id: reqData?.registrationChildID,
      }),
      ...(reqData?.registrationID && {
        ecda_registration_id: reqData?.registrationID,
      }),
      ...(reqData?.isSiblingCda && {
        is_sibling_cda: reqData?.isSiblingCda,
      }),
      ...(reqData?.paymentType && {
        payment_method: reqData?.paymentType,
      }),
      ...(reqData?.referenceNo && {
        reference_no: reqData?.referenceNo,
      }),
      ...(reqData?.remarks && {
        remarks: reqData?.remarks,
      }),
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...RECEIPT.createAdvancePaymentReceiptForEcdaRegistration(reqBody)
    );
    const receipt = resp?.data?.receipt;
    const data =
      receipt &&
      retrieveObjectMapper(receipt, [
        'ID:id',
        'receipt_no',
        'amount',
        'amount_received',
        'credit_amount_used',
        'created_at',
        'paymentType:payment_type_string',
        'pdfUrl:pdf_url_key',
        'preSignedUrl', // missing field.
        'document_no',
        'cancelation_reason',
        'status',
        'remarks',
        'cancelled_date',
        'unlinked_reason',
        'type',
      ]);
    return {
      data: {
        createAdvancePaymentReceiptForEcdaRegistration: data || {},
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCreateECDAAdvancePaymentReceiptForRegistration',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetEcdaRegistrationChildBalanceSheet = async (_, reqData) => {
  try {
    const resp = await financeService().post(
      ...FINANCE.getEcdaRegistrationChildBalanceSheet(reqData)
    );
    return {
      success: true,
      data: {
        getEcdaRegistrationChildBalanceSheet: retrieveObjectMapper(
          get(resp, 'data', {}),
          [
            'cda_credit',
            'non_cda_credit',
            'cda_outstanding',
            'non_cda_outstanding',
          ]
        ),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetEcdaRegistrationChildBalanceSheet',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSkipEcdaRegistrationPaymentStep = async () => {
  try {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          data: {
            skipEcdaRegistrationPaymentStep: true,
          },
          success: true,
        });
      }, 1000);
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSkipEcdaRegistrationPaymentStep',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchAccountInfoByECDAReg = async (_, reqData) => {
  try {
    const ecdaRegChildId = get(reqData, 'IDEcdaRegChild');
    const resp = await financeService().post(
      ...FINANCE.getBankAccountByFkEcdaRegistrationChild(ecdaRegChildId)
    );
    const childIds = [
      ...new Set(
        get(resp, 'data.data', []).map(item =>
          get(item, 'bank_account.fk_child')
        )
      ),
    ].filter(Boolean);

    const bankBicCodeIds = [
      ...new Set(
        get(resp, 'data.data', []).map(item =>
          get(item, 'bank_account.fk_bank_bic_code')
        )
      ),
    ].filter(Boolean);

    const bankAccountIds = [
      ...new Set(
        get(resp, 'data.data', []).map(item => get(item, 'bank_account.id'))
      ),
    ].filter(Boolean);

    const batchResponses = await Promise.all([
      getChildByIds(childIds),
      getBankBicCodeByIds(bankBicCodeIds),
      getBankAccountStateHistoryByAccountIds(bankAccountIds),
      getDocumentTypesByBankAccountIds(bankAccountIds),
    ]);

    return transformFetchAccountInfoByECDAReg(...[resp, ...batchResponses]);
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchAccountInfoByECDAReg',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msGetCreditWriteOffSupportingDocumentUploadURL = async (
  _,
  reqData
) => {
  try {
    const financeSvc = financeService();
    const files = reqData || [];

    const uploadPromises = files.map(async (item, idx) => {
      const fileData = {
        file_name: get(item, 'file.name', ''),
        file_path: get(item, 'file.path', ''),
        file_size: get(item, 'file.size', 0),
        mime_type: get(item, 'file.type', ''),
      };

      try {
        const resp = await financeSvc.post(
          FINANCE.getCreditWriteOffSupportingDocumentUploadURL,
          fileData
        );
        return {
          queryKey: `docUpload${idx + 1}`,
          data: {
            Url: get(resp, 'data.url', ''),
            Key: get(resp, 'data.key', ''),
            Filename: get(resp, 'data.file_name', ''),
          },
        };
      } catch (error) {
        return {
          queryKey: `docUpload${idx + 1}`,
          error: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        };
      }
    });

    const results = await Promise.all(uploadPromises);

    const data = {};
    const errors = [];
    let hasError = false;

    results.forEach(result => {
      if (result.error) {
        hasError = true;
        errors.push({
          message: result.error,
        });
      } else {
        data[result.queryKey] = result.data;
      }
    });

    if (hasError) {
      return {
        success: false,
        error: errors,
        data,
      };
    }

    return {
      success: true,
      data,
      error: null,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetCreditWriteOffSupportingDocumentUploadURL',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msWriteOffCreditBalance = async (_, reqData) => {
  try {
    const reqBody = {
      approver_name: reqData?.dto?.ApproverName,
      cda_amount: reqData?.dto?.CDAAmount,
      child_id: reqData?.dto?.IDChild,
      non_cda_amount: reqData?.dto?.NonCDAAmount,
      reason: reqData?.dto?.Reason,
      requestor_name: reqData?.dto?.RequestorName,
      supporting_document_links: reqData?.dto?.SupportingDocumentLinks,
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.writeOffCreditBalance(reqBody)
    );

    return {
      success: true,
      data: {
        writeOffCreditBalance: resp?.data?.status || false,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msWriteOffCreditBalance',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msWriteOffInvoices = async (_, reqData) => {
  try {
    const reqBody = {
      approver: reqData?.approver,
      body: (reqData?.requestDTOs || []).map(dto => ({
        invoiceID: dto?.invoiceID,
        writeOffAmount: dto?.writeOffAmount,
      })),
      other_reason: reqData?.otherReason,
      reason: reqData?.reason,
      requestor: reqData?.requestor,
      supporting_document_file_keys: reqData?.supportingDocumentFileKey || [],
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(...FINANCE.writeOffInvoices(reqBody));

    const responseData = get(resp, 'data.data', []);
    const transformedData = responseData.map(item => ({
      ID: item.id,
    }));

    return {
      success: true,
      data: {
        writeOffInvoices: {
          data: transformedData,
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msWriteOffInvoices',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCreateOfflineReceipt = async (_, reqData) => {
  try {
    const reqBody = {
      amount: parseFloat(reqData?.amount),
      idInvoice: reqData?.IDInvoice || [],
      isSiblingCda: reqData?.isSiblingCda,
      paymentType: reqData?.paymentType,
      referenceNo: reqData?.referenceNo,
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.createReceiptForOfflinePayment(reqBody)
    );
    return {
      success: true,
      data: {
        createReceiptForOfflinePayment: retrieveObjectMapper(resp?.data || {}, [
          'ID:id',
          'amount',
          'amount_received',
        ]),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCreateOfflineReceipt',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCancelReceipt = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.cancelReceipt({
        receipt_id: get(reqData, 'receiptID'),
        remark: get(reqData, 'remark'),
      })
    );
    return {
      success: get(resp, 'data.cancelled', false),
      data: {
        cancelReceipt: get(resp, 'data.cancelled', false),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCancelReceipt',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUnlinkReceipt = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.unlinkReceipt({
        receipt_id: get(reqData, 'receiptID'),
        remark: get(reqData, 'remark'),
      })
    );
    return {
      success: get(resp, 'data.unlinked', false),
      data: {
        unlinkReceipt: get(resp, 'data.unlinked', false),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUnlinkReceipt',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetECDARegistrationRefundChildBalance = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const params = {
      ...retrieveSortForMS(get(reqData, 'pagination.sort'), '-requestedAt'),
    };
    const resp = await financeSvc.post(
      ...FINANCE.getEcdaRegistrationRefundChildBalance(
        {
          ecda_registration_child_id: get(reqData, 'ecdaRegistrationChildID'),
          ecda_registration_id: get(reqData, 'ecdaRegistrationID'),
        },
        params
      )
    );
    const transformedBalanceData =
      transformGetECDARegistrationRefundChildBalance(get(resp, 'data', []));
    return {
      data: {
        getEcdaRegistrationRefundChildBalance: {
          totalCount: get(resp, 'totalCount', 0),
          data: transformedBalanceData,
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetECDARegistrationRefundChildBalance',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetAllBulkInvoices = async (_, reqData) => {
  try {
    const resp = await financeService()
      .post(
        ...FINANCE.listBulkInvoices(
          {
            ...(get(reqData, 'filter.fkSchool') && {
              fkSchool: [
                {
                  eq: get(reqData, 'filter.fkSchool'),
                },
              ],
            }),
          },
          {
            page: get(reqData, 'pagination.page', 1),
            perPage: get(reqData, 'pagination.perPage', 10),
            ...retrieveSortForMS(get(reqData, 'pagination.sort', [])),
          }
        )
      )
      .then(res => ({
        data: get(res, 'data.data', []),
        totalCount: get(res, 'data.totalCount', 0),
      }))
      .catch(() => ({ data: [], totalCount: 0 }));

    const userIds = uniq(compact(resp.data?.map(d => d.fk_user)));
    const users = await getUserByIds(userIds);
    return {
      success: true,
      data: {
        listBulkInvoiceRequest: {
          data: get(resp, 'data', [])?.map(d => ({
            ...retrieveObjectMapper(d, [
              'ID:id',
              'status:status_string',
              'file_url_key',
              'pre_signed_url',
              'remarks',
              'log',
              'scheduled_at',
              'active',
              'created_at',
              'updated_at',
            ]),
            user: retrieveDataMapper(users, d?.fk_user, [
              'ID:id',
              'firstname',
              'lastname',
            ]),
          })),
          totalCount: get(resp, 'totalCount', 0),
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAllBulkInvoices',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchAdvancePaymentReceiptsForEcdaRegistration = async (
  _,
  reqData
) => {
  try {
    const { pagination } = reqData;
    const reqBody = {
      ecda_registration_child_id: reqData?.ecdaRegistrationChildID,
      ecda_registration_id: reqData?.ecdaRegistrationID,
    };
    const params = {
      page: pagination?.page || 1,
      perPage: pagination?.perPage || 10,
      ...retrieveSortForMS(pagination?.sort || [], 'sort'),
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.getAdvancePaymentReceiptsForEcdaRegistration(reqBody, params)
    );
    const receipts = resp?.data?.data || [];
    return {
      success: true,
      data: {
        getAdvancePaymentReceiptsForEcdaRegistration: {
          data:
            receipts?.map(receipt => ({
              ...retrieveObjectMapper(receipt, [
                'ID:id',
                'receipt_no',
                'amount',
                'amount_received',
                'credit_amount_used',
                'created_at',
                'paymentType',
                'pdfUrl:pdf_url_key',
                'preSignedUrl',
                'document_no',
                'cancelation_reason',
                'status',
                'remarks',
                'cancelled_date',
                'unlinked_reason',
                'type',
              ]),
            })) || [],
          totalCount: resp?.data?.totalCount || 0,
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchAdvancePaymentReceiptsForEcdaRegistration',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msFetchAdvancePaymentReceipts = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const params = {
      page: get(reqData, 'pagination.page'),
      perPage: get(reqData, 'pagination.perPage'),
      ...retrieveSortForMS(get(reqData, 'pagination.sort'), 'sort'),
    };
    const resp = await financeSvc.post(
      ...FINANCE.getAdvancePaymentReceipts(
        {
          child_id: get(reqData, 'childID'),
          filter: {
            fk_centre: get(reqData, 'filter.fkCentre'),
          },
        },
        params
      )
    );
    const transformedData = transformAdvancePaymentReceipts(
      get(resp, 'data.data', [])
    );
    return {
      data: {
        getAdvancePaymentReceipts: {
          data: transformedData,
        },
        totalCount: get(resp, 'data.totalCount'),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchAdvancePaymentReceipts',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUpdateRefundStatus = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.put(...FINANCE.updateRefundStatus(reqData));
    return {
      data: {
        updateRefundStatus: { ID: get(resp, 'data.id', 0) },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUpdateRefundStatus',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msAdjustInvoiceAmountByInvoiceItemsPreview = async (
  _,
  reqData
) => {
  try {
    const financeSvc = financeService();
    const body = {
      adjusted_invoice_item_dto: [
        {
          amount: get(reqData, 'dto[0].Amount'),
          invoice_item_id: get(reqData, 'dto[0].InvoiceItemID'),
        },
      ],
      invoice_id: get(reqData, 'invoiceID'),
    };
    const resp = await financeSvc.post(
      ...FINANCE.adjustInvoiceAmountByInvoiceItemsPreview(body)
    );
    if (get(resp, 'status') !== 200) {
      return {
        data: {
          adjustInvoiceAmountByInvoiceItemsPreview: [],
        },
        success: false,
      };
    }
    return {
      data: {
        adjustInvoiceAmountByInvoiceItemsPreview: {
          data: [
            {
              cdnAmount: get(resp, 'data.data[0].cdn_amount'),
              isCreditNote: get(resp, 'data.data[0].is_credit_note'),
            },
          ],
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msAdjustInvoiceAmountByInvoiceItemsPreview',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        { message: ex?.response?.data?.error || ex?.message || DEFAULT_ERROR },
      ],
    };
  }
};

export const msAdjustInvoiceAmountByInvoiceItems = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const body = {
      adjusted_invoice_item_dto: [
        {
          amount: get(reqData, 'dto[0].Amount'),
          invoice_item_id: get(reqData, 'dto[0].InvoiceItemID'),
        },
      ],
      invoice_id: get(reqData, 'invoiceID'),
      remark: get(reqData, 'remark'),
    };
    const resp = await financeSvc.post(...FINANCE.adjustAmountByItems(body));
    return {
      data: {
        adjustInvoiceAmountByInvoiceItems: get(resp, 'data.is_success'),
      },
      success: get(resp, 'data.is_success'),
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msAdjustInvoiceAmountByInvoiceItems',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchAllRefundsList = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const params = {
      page: get(reqData, 'pagination.page'),
      perPage: get(reqData, 'pagination.perPage'),
      ...retrieveSortForMS(get(reqData, 'pagination.sort')),
    };
    const body = {
      child_id: get(reqData, 'childID'),
      filter: {
        fkCentre: [
          {
            eq: get(reqData, 'filter.fkCentre'),
          },
        ],
      },
    };
    const resp = await financeSvc.post(
      ...FINANCE.getRefundChildBalance(body, params)
    );
    const transformedData = transformRefundList(get(resp, 'data.data', []));
    return {
      data: {
        getRefundChildBalance: {
          data: transformedData,
          totalCount: get(resp, 'data.totalCount', 0),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchAllRefundsList',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetAllRefundChildBalance = async (_, reqData) => {
  try {
    const reqBody = {
      ...(reqData?.childID && {
        child_id: reqData?.childID,
      }),
      filter: {
        ...(reqData?.filter?.status && {
          status: reqData?.filter?.status,
        }),
      },
      ...(reqData?.registrationChildID && {
        registration_child_id: reqData?.registrationChildID,
      }),
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.getAllRefundChildBalance(reqBody)
    );
    const data = resp?.data?.data || [];
    return {
      data: {
        getAllRefundChildBalance: {
          data: transformRefundList(data),
          totalCount: resp?.data?.totalCount || 0,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAllRefundChildBalance',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetAllRefundChildBalanceForECDA = async (_, reqData) => {
  try {
    const reqBody = {
      ...(reqData?.childID && {
        child_id: reqData?.childID,
      }),
      filter: {
        ...(reqData?.filter?.status && {
          status: reqData?.filter?.status,
        }),
      },
      ...(reqData?.ecdaRegistrationChildID && {
        ecda_registration_child_id: reqData?.ecdaRegistrationChildID,
      }),
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.getAllChildBalanceECDA(reqBody)
    );
    const data = resp?.data?.data || [];
    return {
      data: {
        getAllRefundChildBalanceForECDA: {
          data: transformRefundList(data),
          totalCount: resp?.data?.totalCount || 0,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAllRefundChildBalanceForECDA',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCreateAdvancePaymentReceipt = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const body = {
      amount: parseInt(get(reqData, 'amount'), 10),
      centre_id: get(reqData, 'centreID'),
      child_id: get(reqData, 'childID'),
      is_sibling_cda: get(reqData, 'isSiblingCda'),
      payment_method: get(reqData, 'paymentType'),
      reference_no: get(reqData, 'referenceNo'),
      remarks: get(reqData, 'remarks'),
    };
    const resp = await financeSvc.post(
      ...FINANCE.createAdvancePaymentReceipt(body)
    );
    if (get(resp, 'data.receipt')) {
      return {
        data: {
          createAdvancePaymentReceipt: retrieveObjectMapper(
            get(resp, 'data.receipt'),
            [
              'ID:id',
              'amount',
              'amount_received',
              'cancelation_reason',
              'cancelled_date',
              'created_at',
              'credit_amount_used',
              'document_no',
              'paymentType',
              'pdfUrl',
              'preSignedUrl',
              'receipt_no',
              'remarks',
              'status',
              'type',
              'unlinked_reason',
            ]
          ),
        },
        success: true,
      };
    }

    return {
      data: {},
      success: false,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCreateAdvancePaymentReceipt',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCreateAdhocChildRefund = async (_, reqData) => {
  try {
    const reqBody = {
      ...(reqData?.cdaAmount && {
        cda_amount: reqData?.cdaAmount,
      }),
      ...(reqData?.centreID && {
        centre_id: reqData?.centreID,
      }),
      ...(reqData?.childID && {
        child_id: reqData?.childID,
      }),
      ...(reqData?.nonCDAAmount && {
        non_cda_amount: reqData?.nonCDAAmount,
      }),
      ...(reqData?.remark && {
        remark: reqData?.remark,
      }),
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...REFUND.createAdhocChildRefund(reqBody)
    );
    return {
      success: true,
      data: {
        createAdhocChildRefund:
          resp?.data?.refunds?.map(rfnd => ({
            ...retrieveObjectMapper(rfnd, ['ID:id']),
          })) || [],
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCreateAdhocChildRefund',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCreateAdHocCreditNote = async (_, reqData) => {
  try {
    const reqBody = {
      invoice_id: get(reqData, 'IDInvoice'),
      remarks: get(reqData, 'remark'),
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.createAdHocCreditNote(reqBody)
    );
    return {
      data: {
        createAdHocCreditNote: resp?.data?.is_success,
      },
      success: resp?.data?.is_success,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCreateAdHocCreditNote',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        { message: ex?.response?.data?.error || ex?.message || DEFAULT_ERROR },
      ],
    };
  }
};

export const msGetEcdaRegistrationInvoice = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const { registrationID } = reqData;

    const resp = await financeSvc.get(
      ...FINANCE.getEcdaRegistrationInvoice(registrationID)
    );

    const invoiceData = get(resp, 'data', {});
    const invoiceId = get(invoiceData, 'id');

    // initialize default values
    let invoiceItems = { data: [], totalCount: 0 };
    let receipts = { data: [], totalCount: 0 };

    // fetch additional data if invoice ID exists
    if (invoiceId) {
      try {
        const [invoiceItemsResp, receiptsResp] = await Promise.all([
          msGetInvoiceItemByIds(null, {
            invoice_ids: [invoiceId],
          }),
          getReceiptByInvoiceIds([invoiceId]),
        ]);

        // Process invoice items
        if (invoiceItemsResp?.success) {
          invoiceItems = get(invoiceItemsResp, 'data.invoiceItems', {
            data: [],
            totalCount: 0,
          });
        }

        // Process receipts
        if (receiptsResp?.success) {
          const receiptData = get(receiptsResp, `data.${invoiceId}`, []);
          receipts = {
            data: receiptData,
            totalCount: receiptData?.length,
          };
        }
      } catch (additionalDataError) {
        DatadogHandler.addError(additionalDataError);
        DatadogHandler.sendLog(
          additionalDataError,
          {
            functionName: 'msGetEcdaRegistrationInvoice_additionalData',
            fileName,
          },
          'error'
        );
      }
    }

    // Create enhanced response with additional data
    const enhancedResp = {
      data: {
        ...invoiceData,
        invoice_items: invoiceItems,
        receipts,
      },
    };

    const resDataTransform = transformEcdaRegistrationInvoice(enhancedResp);

    return {
      success: true,
      data: {
        getEcdaRegistrationInvoice: resDataTransform,
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetEcdaRegistrationInvoice',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error:
        get(ex, 'response.data.error') || get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const getBillableItems = (body = {}, params = {}) => {
  return financeService()
    .post(...FINANCE.getBillableItems(body, params))
    .then(res => get(res, 'data'))
    .catch(() => ({ data: [], totalCount: 0 }));
};

export const msGetAllApplicableEnquiryDiscounts = async (_, reqBody) => {
  try {
    const regChildId = get(reqBody, 'registrationChildID');
    const resp = await financeService()
      .get(...FINANCE.getApplicableRegChildDiscounts(regChildId))
      .catch(() => ({ data: { data: [], totalCount: 0 } }));

    const discountGroupIds = uniq(
      compact(get(resp, 'data.data', []).map(d => d.fk_discount_item_group))
    );

    const centreIds = uniq(
      compact(get(resp, 'data.data', []).map(d => d.fk_centre))
    );

    const [discountGroups, centres] = await Promise.all([
      getDiscountItemGroupByIds(discountGroupIds),
      getCentreByIds(centreIds),
    ]);

    return transformApplicableEnquiryDiscounts(resp, discountGroups, centres);
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetAllApplicableEnquiryDiscounts',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetAllApplicableEnquiryDiscountsEcda = async (_, reqBody) => {
  try {
    const regChildId = get(reqBody, 'registrationChildID');

    const resp = await financeService()
      .get(...FINANCE.getApplicableDiscountsEcda(regChildId))
      .catch(() => ({ data: { data: [], totalCount: 0 } }));

    const discountGroupIds = uniq(
      compact(get(resp, 'data.data', []).map(d => d.fk_discount_item_group))
    );

    const centreIds = uniq(
      compact(get(resp, 'data.data', []).map(d => d.fk_centre))
    );

    const [discountGroups, centres] = await Promise.all([
      getDiscountItemGroupByIds(discountGroupIds),
      getCentreByIds(centreIds),
    ]);

    return transformApplicableEnquiryDiscounts(
      resp,
      discountGroups,
      centres,
      true
    );
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAllApplicableEnquiryDiscountsEcda',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message') || DEFAULT_ERROR,
    };
  }
};

export const msFetchAllPurchaseItems = async (_, reqData) => {
  try {
    const sort = reqData.pagination?.sort?.toString();
    const perPage = reqData.pagination?.perPage || MAX_PER_PAGE;
    const res = await financeService().post(
      FINANCE.findAllPurchaseItem,
      {
        centre: reqData.IDCentre,
      },
      { params: { perPage, sort } }
    );

    return {
      success: true,
      data: {
        findAllPurchaseItem: {
          data: get(res, 'data.data', []).map(d =>
            convertObjToCamelCase(d, ['type:type_string'])
          ),
          totalCount: get(res, 'data.totalCount', 0),
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchAllPurchaseItems',
        fileName,
      },
      'error'
    );

    throw ex;
  }
};

export const msSearchInvoice = async (_, reqData) => {
  try {
    const { invoiceFilter, ftsInput, refundOnly } = reqData;
    const filter = {};
    if (invoiceFilter?.fkChild) {
      filter.fk_child = [
        {
          eq: invoiceFilter?.fkChild,
        },
      ];
    }
    if (invoiceFilter?.fkCentre) {
      filter.fk_centre = [
        {
          eq: invoiceFilter?.fkCentre,
        },
      ];
    }
    if (invoiceFilter?.status) {
      filter.status = invoiceFilter?.status;
    }
    if (refundOnly) {
      filter.invoice_type = [
        {
          eq: 'refund',
        },
      ];
    }

    const res = await financeService().post(FINANCE.searchInvoice, {
      fts_input: ftsInput,
      filter,
    });

    return {
      success: true,
      data: {
        searchInvoice: {
          data: get(res, 'data.data', []).map(d =>
            convertObjToCamelCase(d, [
              'status:status_string',
              'invoice_type:invoice_type_string',
            ])
          ),
          totalCount: get(res, 'data.totalCount', 0),
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSearchInvoice',
        fileName,
      },
      'error'
    );

    throw ex;
  }
};

export const msSearchFinance = async (_, reqData) => {
  try {
    const { ftsInput, refundOnly } = reqData;

    const requestBody = {
      fts_input: ftsInput || '',
      refund_only: refundOnly || false,
    };

    const res = await financeService().post(FINANCE.searchFinance, requestBody);
    return {
      success: true,
      data: {
        searchFinance: {
          data: get(res, 'data.data', []).map(d =>
            convertObjToCamelCase(d, [], ['linkedInvoiceID:linked_invoice_ids'])
          ),
          totalCount: get(res, 'data.data', []).length,
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSearchFinance',
        fileName,
      },
      'error'
    );

    throw ex;
  }
};

export const msValidateProgramBillingRequest = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.validateProgramBillingRequest, {
      file_path: get(reqData, 'filePath'),
    });
    return {
      data: {
        validateProgramBillingRequest: resp?.data,
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCreateAdHocCreditNote',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetAllProgramBillings = async (_, reqData) => {
  try {
    const { filter, pagination } = reqData;
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      FINANCE.findAllProgramBillingUpload,
      {
        school: [
          {
            eq: filter?.fkSchool,
          },
        ],
      },
      {
        params: {
          page: pagination?.page,
          perPage: pagination?.perPage || 20,
          sort: camelToSnake(pagination?.sort?.toString()),
        },
      }
    );
    const data = resp?.data || {};
    const userIds = uniq(data?.data.map(d => d?.fk_user));
    const users = await getUserByIds(userIds);
    const userMap = keyBy(users, 'id');

    return {
      success: true,
      data: {
        findAllProgramBillingUpload: {
          data: data?.data.map(d => ({
            ...convertObjToCamelCase(d, [
              'status:status_string',
              'docType:doc_type_string',
            ]),
            user: userMap[d?.fk_user],
          })),
          totalCount: data?.totalCount || 0,
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAllProgramBillings',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetFeeTiersByLevelByCentre = async (_, params) => {
  try {
    const { levelID, centreID, date } = params;
    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.getFeeTiersByLevelByCentre, {
      centreID,
      enrolmentDate: date,
      levelID,
    });
    const data = resp?.data?.data || [];
    return {
      data: {
        getFeeTiersByLevelByCentre: {
          data: data.map(d => convertObjToCamelCase(d)),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetFeeTiersByLevelByCentre',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetAllFeeTiers = async filters => {
  try {
    const { id } = filters || {};
    const filter = {};
    if (id) {
      filter.id = [
        {
          eq: id,
        },
      ];
    }

    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.getAllFeeTiers, {
      filter,
    });
    const data = resp?.data?.data || [];
    return {
      data: data.map(d => convertObjToCamelCase(d)),
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAllFeeTiers',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetChildFeeTierForChild = async (_, params) => {
  try {
    const { childID, date } = params;
    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.getChildFeeTierForChild, {
      childId: childID,
      date,
    });
    const data = resp?.data || {};

    const allFeeTiers = await msGetAllFeeTiers({
      id: [data.fk_fee_tier],
    });
    const feeTierMap = keyBy(allFeeTiers.data, 'ID');
    return {
      data: {
        getChildFeeTierForChild: {
          ...convertObjToCamelCase(data),
          feeTier: convertObjToCamelCase(feeTierMap[data.fk_fee_tier]),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetChildFeeTierForChild',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msUpComingChildFeeTierForChild = async (_, params) => {
  try {
    const { childID } = params;
    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.getUpcomingChildFeeTier, {
      childId: childID,
    });
    const data = resp?.data;

    if (!data) {
      return {
        data: {
          getUpcomingChildFeeTierForChild: null,
        },
        success: true,
      };
    }

    const allFeeTiers = await msGetAllFeeTiers({
      id: [data.fk_fee_tier],
    });
    const feeTierMap = keyBy(allFeeTiers.data, 'ID');
    return {
      data: {
        getUpcomingChildFeeTierForChild: {
          ...convertObjToCamelCase(data),
          feeTier: convertObjToCamelCase(feeTierMap[data.fk_fee_tier]),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetUpcomingChildFeeTierForChild',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetProgramBillingUploadURL = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      FINANCE.getProgramBillingUploadURL,
      convertObjToSnakeCase(reqData?.file)
    );
    const data = resp?.data || {};
    return {
      data: {
        getProgramBillingUploadURL: {
          Filename: data?.file_name,
          Url: data?.url,
          Key: data?.key,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetProgramBillingUploadURL',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetFinanceDocumentUploadURL = async (_, reqData) => {
  try {
    const financeSvc = financeService();

    const body = {
      doc_type: reqData?.docType,
      file_name: reqData?.file?.fileName || reqData?.file?.name,
      file_path: reqData?.file?.filePath || reqData?.docType,
      file_size: reqData?.file?.fileSize || reqData?.file?.size,
      mime_type: reqData?.file?.mimeType || reqData?.file?.mimeType,
    };
    const resp = await financeSvc.post(
      FINANCE.getFinanceDocumentUploadURL,
      body
    );
    const data = resp?.data || {};
    return {
      data: {
        getFinanceDocumentUploadURL: {
          Filename: data?.file_name,
          Url: data?.url,
          Key: data?.key,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetFinanceDocumentUploadURL',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error:
        get(ex, 'response.data.error') || get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msCreateProgramBillingRequest = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.createProgramBillingRequest, {
      file_path: reqData?.filePath,
    });
    const data = resp?.data || {};
    return {
      data: {
        createProgramBillingRequest: {
          ID: data?.id,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetProgramBillingUploadURL',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetAllActiveSubsidies = async (_, reqData) => {
  try {
    const params = {
      centre_id: reqData?.centreID,
      current_active: reqData?.currentActive || false,
    };
    const financeSvc = financeService();
    const resp = await financeSvc.get(
      FINANCE.findAllChildActiveSubsidies(reqData?.childID, params)
    );
    return {
      success: true,
      data: {
        findAllChildActiveSubsidies: {
          totalCount: resp?.data?.total_count || 0,
          data:
            resp?.data?.data?.map(d => ({
              ...retrieveObjectMapper(d, [
                'label',
                'creator',
                'creatorID:creator_id',
                'fkChild:child_id',
                'fkCentre:centre_id',
                'idCustomSubsidy:custom_subsidy_id',
                'fkSubsidyArrangement:subsidy_arrangement_id',
                'fkBillableItem:billable_item_id',
                'disburse_mode',
                'mode',
                'amount',
                'editable',
                'voidable',
                'custom_subsidy_status',
                'status',
                'from',
                'to',
                'created_at',
                'remarks',
              ]),
            })) || [],
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAllActiveSubsidies',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetAllCustomSubsidyType = async (_, { childID, centreID }) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.get(
      FINANCE.findAllCustomSubsidies(childID, centreID)
    );
    return {
      success: true,
      data: {
        findAllCustomSubsidies: {
          totalCount: resp?.data?.TotalCount || 0,
          data:
            resp?.data?.Data?.map(d => ({
              ...retrieveObjectMapper(d, [
                'ID:id',
                'fkCentre:centre_id',
                'subsidy_type',
                'label',
              ]),
            })) || [],
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAllCustomSubsidyType',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetHHIHistory = async (_, reqData) => {
  try {
    const childId = get(reqData, 'childID');
    const pagination = get(reqData, 'pagination', {});
    const page = get(pagination, 'page', DEFAULT_PAGE);
    const perPage = get(pagination, 'perPage', DEFAULT_ITEMS_PER_PAGE);

    const resp = await financeService().post(
      ...FINANCE.getHHIHistory({ child_id: childId }, { page, perPage })
    );

    const responseData = get(resp, 'data', {});
    const totalCount = get(responseData, 'totalCount', 0);
    const data = get(responseData, 'data', []);

    return {
      success: true,
      data: {
        getHHIHistory: {
          totalCount,
          data: data.map(item => ({
            forMonth: get(item, 'forMonth'),
            incomeLevel: get(item, 'incomeLevel'),
          })),
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetHHIHistory',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetHistoryBillingAdjustments = async (_, reqData) => {
  try {
    const params = {
      ...(reqData?.centreID && {
        centre_id: reqData?.centreID,
      }),
      ...(reqData?.fromDate && {
        from_date: moment(reqData?.fromDate).format(DATE_PICKER_FORMAT),
      }),
      ...(reqData?.toDate && {
        to_date: moment(reqData?.toDate).format(DATE_PICKER_FORMAT),
      }),
    };
    const financeSvc = financeService();
    const resp = await financeSvc.get(
      FINANCE.findAllChildHistorySubsidies(reqData?.childID, params)
    );
    return {
      success: true,
      data: {
        findAllChildHistorySubsidiesForBillingAdjustment: {
          totalCount: resp?.data?.total_count || 0,
          data: transformChildSubsidyHistories(resp?.data?.data || []),
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetHistoryBillingAdjustments',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msExportProgramBillingUploadTemplate = async () => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.get(
      FINANCE.exportProgramBillingUploadTemplate
    );
    const data = resp?.data || {};
    return {
      data: {
        exportProgramBillingUploadTemplate: data?.filePath,
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msExportProgramBillingUploadTemplate',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetApplicableFeeTiersForChild = async (_, reqData) => {
  try {
    const { centreID, childID } = reqData;
    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.getApplicableFeeTiersForChild, {
      centreId: centreID,
      childId: childID,
    });
    const data = resp?.data?.data || [];

    return {
      data: {
        getApplicableFeeTiersForChild: {
          data: data.map(d => convertObjToCamelCase(d)),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetApplicableFeeTiersForChild',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msCancelBulkInvoiceRequest = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.cancelBulkInvoiceRequest, {
      bulk_invoice_id: reqData.IDBulkInvoice,
      remarks: reqData.remarks,
    });
    return {
      data: {
        cancelBulkInvoiceRequest: { ID: get(resp, 'data.id', 0) },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCancelBulkInvoiceRequest',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: ex?.response?.data?.error || ex?.message || DEFAULT_ERROR,
          errCode: ex?.response?.data?.errCode || ex?.code,
        },
      ],
    };
  }
};

export const msListBulkFeeUploadRequest = async (_, reqData) => {
  try {
    const { pagination } = reqData;
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      FINANCE.listBulkFeeUploadRequest,
      {},
      {
        params: {
          page: pagination?.page || 1,
          size: pagination?.perPage || 10,
          sort: pagination?.sort?.toString(),
        },
      }
    );
    const list = resp?.data?.data || [];
    const userIds = uniq(
      list.map(d => d?.fee_bulk_upload?.created_by_fk_staff)
    );
    const users = await getUserByIds(userIds);
    const userMap = keyBy(users, 'id');

    return {
      data: {
        listBulkFeeUploadRequest: {
          totalCount: resp?.data?.total_count || 0,
          data: list.map(d => ({
            ...convertObjToCamelCase(d.fee_bulk_upload, [
              'status:status_string',
              'pdfURLKey:pdf_url_key',
            ]),
            preSignedUrl: d?.presigned_url,
            user: {
              ID: d?.fee_bulk_upload?.created_by_fk_staff,
              firstname:
                userMap[d?.fee_bulk_upload?.created_by_fk_staff]?.firstname,
              lastname:
                userMap[d?.fee_bulk_upload?.created_by_fk_staff]?.lastname,
            },
          })),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msListBulkFeeUploadRequest',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetBulkFeeUploadTemplate = async (_, centreIds) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.getBulkFeeUploadTemplate, {
      centre_ids: centreIds,
    });
    const data = resp?.data || {};
    return {
      data: {
        getBulkFeeUploadTemplate: data?.template,
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetBulkFeeUploadTemplate',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetBulkFeeUploadURL = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      FINANCE.getBulkFeeUploadURL,
      convertObjToSnakeCase(reqData?.file)
    );
    const data = resp?.data || {};
    return {
      data: {
        getBulkFeeUploadURL: {
          Filename: data?.file_name,
          Url: data?.url,
          Key: data?.key,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetBulkFeeUploadURL',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msFetchAllUploadedSubsidyFiles = async (_, reqData) => {
  try {
    const params = {
      page: get(reqData, 'pagination.page'),
      perPage: get(reqData, 'pagination.perPage'),
      ...retrieveSortForMS(get(reqData, 'pagination.sort'), 'sort'),
    };
    const resp = await financeService().get(
      FINANCE.findAllUploadedSubsidyFiles,
      params
    );
    const userIds = uniq(
      compact(get(resp, 'data.data', []).map(d => d.fk_user))
    );
    const users = await getUserByIds(userIds);
    return {
      data: {
        findAllUploadedSubsidyFiles: {
          data: (get(resp, 'data.data') || []).map(d => ({
            ...retrieveObjectMapper(d, [
              'ID:id',
              'label',
              'filename',
              'uRL:url',
              'status:status_string',
              'log',
              'created_at',
              'presigned_url', // Missing
            ]),
            user: retrieveDataMapper(users, d.fk_user, [
              'ID:id',
              'firstname',
              'lastname',
            ]),
          })),
          totalCount: get(resp, 'data.totalCount', 0),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchAllUploadedSubsidyFiles',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUploadSubsidyFile = async (_, reqData) => {
  try {
    const payload = convertObjToSnakeCase(
      reqData,
      [],
      ['id_School:IDSchool', 'url:url', 'filename:filename']
    );

    const resp = await financeService().post(
      ...FINANCE.uploadSubsidyFile(payload)
    );

    return {
      success: true,
      data: {
        uploadSubsidyFile: convertObjToCamelCase(
          get(resp, 'data', null),
          [],
          ['ID:id']
        ),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUploadSubsidyFile',
        fileName,
      },
      'error'
    );
    const fullErrMsg = get(ex, 'response.data.error', DEFAULT_ERROR);
    return {
      success: false,
      error: [{ message: fullErrMsg }],
    };
  }
};

export const msExportProgramBillingMasterFile = async (_, reqData) => {
  try {
    const resp = await financeService().get(
      FINANCE.downloadProgramBillingMasterFile,
      {
        forDsp: get(reqData, 'forDsp', false),
      }
    );
    return {
      data: {
        exportProgramBillingMasterFile: get(resp, 'data.filePath', ''),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msExportProgramBillingMasterFile',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
        },
      ],
    };
  }
};

export const msGetChildSOAByYear = async (_, reqData) => {
  const { childID, year } = reqData;
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.get(
      FINANCE.GET_STATEMENT_OF_ACCOUNT(childID, year)
    );
    return {
      success: true,
      loading: false,
      data: {
        getChildSOAByYear: {
          totalCount: resp?.data?.TotalCount || 0,
          data:
            resp?.data?.data?.map(d => ({
              ...retrieveObjectMapper(d, [
                'fk_child',
                'ID:id',
                'fk_centre',
                'date',
                'uRL:url',
                'cda_outstanding',
                'non_cda_outstanding',
                'total_outstanding',
                'created_at',
                'updated_at',
              ]),
            })) || [],
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetChildSOAByYear',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msUploadBulkFeeFile = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const body = {
      file_url: get(reqData, 'fileUrl'),
      file_name: get(reqData, 'fileName'),
      effective_date: get(reqData, 'effectiveDate'),
    };

    const resp = await financeSvc.post(...FINANCE.uploadBulkFeeFile(body));

    return {
      data: {
        uploadBulkFeeFile: {
          ID: get(resp, 'data.id'),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUploadBulkFeeFile',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error:
        get(ex, 'response.data.error') || get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetTaxRateAndAmount = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.post(FINANCE.getTaxRateAndAmount, {
      items: reqData?.dto?.Items.map(item => ({
        billable_item_id: item?.BillableItemId,
        quantity: item?.Quantity,
        period: item?.Period,
        unit_price: item?.UnitPrice,
      })),
    });
    return {
      data: {
        getTaxRateAndAmount: {
          Items: get(resp, 'data.items').map(item => ({
            TaxRate: get(item, 'tax_rate'),
            TaxAmount: get(item, 'tax_amount'),
            TotalAmount: get(item, 'total_amount'),
            BillableItemId: get(item, 'billable_item_id'),
            Quantity: get(item, 'quantity'),
            Period: get(item, 'period'),
            UnitPrice: get(item, 'unit_price'),
            Label: get(item, 'label'),
            Type: get(item, 'type'),
          })),
          TotalAmount: get(resp, 'data.total_amount'),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetTaxRateAndAmount',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetAllApplicableChildDiscounts = async (_, childID) => {
  try {
    const financeSvc = financeService();
    const resp = await financeSvc.get(
      FINANCE.findAllApplicableChildDiscounts(childID)
    );
    const data = resp?.data?.data || [];
    const centreIds = data.map(d => d?.fk_centre);
    const discountGroupIds = data.map(d => d?.fk_discount_item_group);
    const [discountGroups, centres] = await Promise.all([
      getDiscountItemGroupByIds(discountGroupIds),
      getCentreByIds(centreIds),
    ]);
    const centreMap = keyBy(centres?.data, 'id');
    const discountGroupMap = keyBy(discountGroups, 'id');

    return {
      data: {
        findAllApplicableChildDiscounts: {
          data: data.map(d => ({
            ...convertObjToCamelCase(d, [
              'applicationLevel:application_level_string',
              'disbursementMode:disbursement_mode_string',
              'discountItemType:discount_item_type_string',
              'type:type_string',
            ]),
            centre: {
              ID: d?.fk_centre,
              label: get(centreMap, `${d?.fk_centre}.label`),
            },
            discountItemGroup: convertObjToCamelCase(
              get(discountGroupMap, `${d?.fk_discount_item_group}`),
              [
                'discountItemType:discount_item_type_string',
                'applicationLevel:application_level_string',
                'status:status_string',
                'applyTo:apply_to_string',
              ]
            ),
          })),
          totalCount: get(resp, 'data.TotalCount', 0),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetTaxRateAndAmount',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetAllConsolidatedRefund = async (_, reqData) => {
  try {
    const params = {
      page: get(reqData, 'pagination.page'),
      perPage: get(reqData, 'pagination.perPage'),
      ...retrieveSortForMS(get(reqData, 'pagination.sort'), 'sort'),
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.GET_REFUND_CONSOLIDATED(
        {
          doc_type: get(reqData, 'docType'),
          ...(get(reqData, 'refundCBalFilter') && {
            invoice_filter: {
              refund_c_bal_filter: {
                status: get(reqData, 'refundCBalFilter.status', []),
              },
            },
          }),
        },
        params
      )
    );
    return {
      data: {
        findAllConsolidatedRefund: {
          data: get(resp, 'data.data', []).map(item => {
            return retrieveObjectMapper(item, [
              'ID:id',
              'date',
              'refundCompletionDate',
              'label',
              'IDChild:child_id',
              'IDRegistrationChild:registration_child_id',
              'IDRegistration:registration_id',
              'IDEcdaRegistrationChild:ecda_registration_child_id',
              'IDEcdaRegistration:ecda_registration_id',
              'child_firstname',
              'child_lastname',
              'associated_items',
              'amount',
              'type',
              'base_type',
              'status',
            ]);
          }),
          totalCount: get(resp, 'data.totalCount', 0),
        },
      },
      success: true,
      error: null,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAllConsolidatedRefund',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msCreateCustomSubsidy = async (_, reqData) => {
  try {
    const reqBody = convertObjToSnakeCase(reqData?.dto, [
      'centre_id:centreID',
      'child_id:childID',
      'billable_item_id:billableItemID',
      'invoice_id:invoiceID',
    ]);
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.addCustomSubsidyToChild(reqBody)
    );
    return {
      data: {
        addCustomSubsidyToChild: {
          ...convertObjToCamelCase(resp?.data),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCreateCustomSubsidy',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          extensions: {
            errorCode: get(ex, 'response.data.errCode', get(ex, 'code')),
          },
        },
      ],
    };
  }
};

export const msVoidCustomSubsidy = async (_, reqData) => {
  try {
    const reqBody = convertObjToSnakeCase(reqData, [
      'subsidy_arrangement_id:subsidyArrangementID',
    ]);
    const financeSvc = financeService();
    const resp = await financeSvc.post(...FINANCE.voidCustomSubsidy(reqBody));
    return {
      data: {
        voidCustomSubsidy: resp?.data && convertObjToCamelCase(resp?.data),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msVoidCustomSubsidy',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
        },
      ],
    };
  }
};

export const msRemoveCustomSubsidy = async (_, reqData) => {
  try {
    // TODO: Replace with actual MS endpoint when available
    // Mock implementation for now
    const { IDChild, subsidyArrangementID } = reqData;

    // Simulating MS response structure
    const mockResponse = {
      success: true,
      data: {
        removeCustomSubsidy: {
          success: true,
          childId: IDChild,
          subsidyArrangementId: subsidyArrangementID,
          message: `Successfully removed custom subsidy (Mock MS Response) - Child ID: ${IDChild}, Subsidy Arrangement ID: ${subsidyArrangementID}`,
        },
      },
    };

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 130));

    return mockResponse;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msRemoveCustomSubsidy',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
        },
      ],
    };
  }
};

export const msExemptCustomSubsidy = async (_, reqData) => {
  try {
    // TODO: Replace with actual MS endpoint when available
    // Mock implementation for now
    const { IDChild, subsidyArrangementID, exemptReason } = reqData;

    // Simulating MS response structure
    const mockResponse = {
      success: true,
      data: {
        exemptCustomSubsidy: {
          success: true,
          childId: IDChild,
          subsidyArrangementId: subsidyArrangementID,
          exemptReason,
          message: `Successfully exempted custom subsidy (Mock MS Response) - Child ID: ${IDChild}, Reason: ${
            exemptReason || 'N/A'
          }`,
        },
      },
    };

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 140));

    return mockResponse;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msExemptCustomSubsidy',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
        },
      ],
    };
  }
};

export const msCancelCustomSubsidy = async (_, reqData) => {
  try {
    // TODO: Replace with actual MS endpoint when available
    // Mock implementation for now
    const { customSubsidyID, remark } = reqData;

    // Simulating MS response structure
    const mockResponse = {
      success: true,
      data: {
        cancelCustomSubsidy: {
          success: true,
          customSubsidyId: customSubsidyID,
          remark,
          message: `Successfully cancelled custom subsidy (Mock MS Response) - Subsidy ID: ${customSubsidyID}`,
        },
      },
    };

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 135));

    return mockResponse;
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCancelCustomSubsidy',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
        },
      ],
    };
  }
};

export const msRejectCustomSubsidy = async (_, reqData) => {
  try {
    const reqBody = convertObjToSnakeCase(reqData, [
      'custom_subsidy_id:customSubsidyID',
      'remarks:remark',
    ]);
    const financeSvc = financeService();
    const resp = await financeSvc.post(...FINANCE.rejectCustomSubsidy(reqBody));
    return {
      data: {
        rejectCustomSubsidy: resp?.data && convertObjToCamelCase(resp?.data),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msRejectCustomSubsidy',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msEditHistorySubsidy = async (_, reqData) => {
  try {
    const { billableItemID, forMonth, amount, childID, ...restData } = reqData;
    const transformedForMonth = forMonth
      ? moment(forMonth).format('YYYY-MM')
      : forMonth;
    const transformedAmount = amount != null ? parseFloat(amount) : amount;
    const reqBody = convertObjToSnakeCase(
      {
        ...restData,
        billableItemID,
        forMonth: transformedForMonth,
        amount: transformedAmount,
      },
      [
        'invoice_id:invoiceID',
        'invoice_item_id:invoiceItemID',
        'billable_item_id:billableItemID',
        'for_month:forMonth',
      ]
    );
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      FINANCE.adjustMonthlyFeeInvoice(childID),
      reqBody
    );
    return {
      data: {
        adjustMonthlyFeeInvoiceByBillableItemID: get(
          resp,
          'data.is_success',
          false
        ),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msEditHistorySubsidy',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          extensions: {
            errorCode: get(ex, 'response.data.errCode', get(ex, 'code')),
          },
        },
      ],
    };
  }
};

export const msGetChildFinancialAssistanceStatus = async (_, reqData) => {
  try {
    const financeSvc = financeService();

    const resp = await financeSvc.post(
      ...FINANCE.getChildFinancialAssistanceStatus({
        centre_id: get(reqData, 'centreID'),
        child_id: get(reqData, 'childID'),
      })
    );

    return {
      data: {
        getChildFinancialAssistanceStatus: {
          msfSUGStatus: get(resp, 'data.msf_sug_status'),
          bhfSUGStatus: get(resp, 'data.bhf_sug_status'),
          referralType: get(resp, 'data.referral_type'),
          faCoPaymentAmount: get(resp, 'data.fa_co_payment_amount'),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetChildFinancialAssistanceStatus',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error:
        get(ex, 'response.data.error') || get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGiroSiblingSelectionAction = async (_, reqData) => {
  try {
    const financeSvc = financeService();

    const resp = await financeSvc.post(
      ...FINANCE.giroSiblingSelectionAction({
        ...(get(reqData, 'IDRegChild') && {
          registration_child_id: get(reqData, 'IDRegChild'),
        }),
        ...(get(reqData, 'IDEcdaRegChild') && {
          ecda_registration_child_id: get(reqData, 'IDEcdaRegChild'),
        }),
        is_sibling_cda: get(reqData, 'IsSiblingCda'),
      })
    );

    return {
      data: {
        giroSiblingSelectionAction: get(resp, 'data.success', false),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGiroSiblingSelectionAction',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error:
        get(ex, 'response.data.error') || get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetSiblingBirthCertificateTempUploadURL = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const file = get(reqData, 'file', {});

    const queryParams = {};
    if (get(reqData, 'IDEcdaRegChild')) {
      queryParams.ecdaRegChildId = get(reqData, 'IDEcdaRegChild');
    }
    if (get(reqData, 'IDRegChild')) {
      queryParams.regChildId = get(reqData, 'IDRegChild');
    }
    if (get(reqData, 'IDChild')) {
      queryParams.childId = get(reqData, 'IDChild');
    }

    const resp = await financeSvc.post(
      ...FINANCE.getSiblingBirthCertificateTempUploadURL(
        {
          file_name: get(file, 'filename'),
          mime_type: get(file, 'mimetype'),
          file_path: get(file, 'filepath'),
          file_size: get(file, 'filesize'),
        },
        queryParams
      )
    );

    return {
      data: {
        getSiblingBirthCertificateTempUploadURL: {
          Url: get(resp, 'data.url'),
          Key: get(resp, 'data.key'),
          Filename: get(resp, 'data.filename'),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetSiblingBirthCertificateTempUploadURL',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error:
        get(ex, 'response.data.error') || get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchUserAccounts = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const body = {
      payment_type: reqData?.paymentType,
      is_cda: reqData?.isCDA,
    };
    const resp = await financeSvc.post(
      ...FINANCE.getRegistrationAccountListing(body)
    );
    return {
      data: {
        getRegistrationAccountListing:
          get(resp, 'data.accounts.length', 0) > 0 &&
          get(resp, 'data.accounts', []).map(item => {
            return {
              AccountName: item?.account_name,
              AccountNumber: item?.account_number,
              AccountBankCode: item?.account_bank_code,
              AccountBranchCode: item?.account_branch_code,
              AccountType: item?.account_type,
            };
          }),
      },
      loading: false,
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchUserAccounts',
        fileName,
      },
      'error'
    );
    return {
      data: {
        getRegistrationAccountListing: null,
      },
      loading: false,
      success: false,
    };
  }
};

export const getTradeDebtNotificationConfigByChildId = childId => {
  if (!childId) return [];
  return financeService()
    .get(FINANCE.getTradeDebtNotificationConfigByChildId(childId))
    .then(r => get(r, 'data', {}))
    .catch(() => ({}));
};

export const msMarkChildToGenerateGiroRefund = async (_, reqData) => {
  try {
    const { IDRefundChildBalance, isSelectAll } = reqData;
    const payload = convertObjToSnakeCase({
      idRefundChildBalance: IDRefundChildBalance,
      isSelectAll,
    });

    const resp = await financeService().post(
      FINANCE.markGenerateGiroRefund,
      payload
    );

    return {
      data: {
        markChildToGenerateGiroRefund: get(resp, 'data.success', false),
      },
      success: get(resp, 'data.success', false),
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msMarkChildToGenerateGiroRefund',
        fileName: 'src/services/FinanceService.js',
      },
      'error'
    );
    return {
      success: false,
      error: get(
        error,
        'response.data.error',
        get(error, 'message', DEFAULT_ERROR)
      ),
    };
  }
};

export const msUnmarkChildToGenerateGiroRefund = async (_, reqData) => {
  try {
    // TODO: Replace with actual MS endpoint when available
    // Mock implementation for now
    const { IDRefundChildBalance, isSelectAll } = reqData;

    // Simulating MS response structure
    const mockResponse = {
      success: true,
      data: {
        unmarkChildToGenerateGiroRefund: true,
        message: `Successfully unmarked refund (Mock MS Response) - IDs: ${
          IDRefundChildBalance?.join(', ') || 'All'
        }, SelectAll: ${isSelectAll}`,
        unmarkedCount: isSelectAll ? 'all' : IDRefundChildBalance?.length || 0,
      },
    };

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 120));

    return mockResponse;
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUnmarkChildToGenerateGiroRefund',
        fileName: 'src/services/FinanceService.js',
      },
      'error'
    );
    return {
      success: false,
      error: get(
        error,
        'response.data.error',
        get(error, 'message', DEFAULT_ERROR)
      ),
    };
  }
};

export const getLinkpointAwardConfigsByCentreIds = (centreIds = []) => {
  if (centreIds.length === 0) {
    return [];
  }
  return financeService()
    .post(FINANCE.getLinkpointAwardConfigsByCentreIds, {
      centreIds,
    })
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const getLinkpointAwardConfigDraftsByCentreIds = (
  centreDraftIds = []
) => {
  if (centreDraftIds.length === 0) {
    return [];
  }
  return financeService()
    .post(FINANCE.getLinkpointAwardConfigDraftsByCentreIds, {
      centre_draft_ids: centreDraftIds,
    })
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const msPayRegistrationInvoiceByGiro = async (_, reqBody = {}) => {
  try {
    const invoiceID = get(reqBody, 'IDInvoice');
    const resp = await financeService().get(
      FINANCE.payRegistrationInvoiceByGiro,
      { invoiceID }
    );
    return {
      success: true,
      data: {
        payRegistrationInvoiceByGiro: convertObjToCamelCase(
          get(resp, 'data', {})
        ),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msPayRegistrationInvoiceByGiro',
        fileName,
      },
      'error'
    );
    return { success: false, error };
  }
};

export const msGetAllAbsentChildVoidSubsidy = async (_, reqData) => {
  try {
    if (!reqData?.childID) return { data: {}, success: true };

    const params = { centre_id: reqData?.centreID };
    const resp = await financeService().get(
      FINANCE.getAllAbsentForVoidingSubsidy(reqData?.childID, params)
    );

    return {
      data: {
        findAllAbsentForVoidingSubsidyByChild: {
          totalCount: resp?.data?.total_count || 0,
          data:
            resp?.data?.data?.map(dt => ({
              ...retrieveObjectMapper(dt, [
                'centreID:centre_id',
                'childID:child_id',
                'month',
              ]),
            })) || [],
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAllAbsentChildVoidSubsidy',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error:
        get(ex, 'response.data.error') || get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUpdateChildFinancialAssistance = async (_, reqBody = {}) => {
  try {
    if (!reqBody?.childID) return { success: true, data: {} };

    const body = {
      centre_id: reqBody?.centreID,
      child_id: reqBody?.childID,
      data: convertObjToSnakeCase(reqBody?.dto, [
        'bhf_sug_status:bhfSUGStatus',
        'msf_sug_status:msfSUGStatus',
      ]),
    };
    const resp = await financeService().post(
      ...FINANCE.updateFinancialAssistanceStatus(body)
    );

    return {
      success: true,
      data: {
        updateChildFinancialAssistanceStatus: resp?.data?.success || false,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateChildFinancialAssistance',
        fileName,
      },
      'error'
    );
    return error;
  }
};

export const msUploadGiroFile = async (_, reqData) => {
  try {
    const body = {
      url: reqData?.url,
      doc_type: reqData?.docType,
      file_name: reqData?.filename,
      id_request_finance_document: reqData?.IDRequestFinanceDocument,
    };
    const resp = await financeService().post(...FINANCE.uploadGiroFile(body));
    return {
      data: {
        uploadGiroFile: resp?.data && convertObjToCamelCase(resp?.data),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUploadGiroFile',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
        },
      ],
    };
  }
};

export const msGetDebtWriteOffDocUploadUrls = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const files = reqData || [];

    if (files.length === 0) {
      return {
        success: true,
        data: {},
      };
    }

    const uploadPromises = files.map(async (item, idx) => {
      try {
        const body = {
          file_name: get(item, 'file.name'),
          file_path: get(item, 'file.path'),
          file_size: get(item, 'file.size'),
          mime_type: get(item, 'file.type'),
        };

        const resp = await financeSvc.post(
          ...FINANCE.getDebtWriteOffDocUploadURL(body)
        );

        const responseData = resp?.data || {};
        return {
          queryKey: `docUpload${idx + 1}`,
          data: {
            Url: responseData?.url,
            Key: responseData?.key,
            Filename: responseData?.file_name,
          },
        };
      } catch (ex) {
        DatadogHandler.addError(ex);
        DatadogHandler.sendLog(
          ex,
          {
            functionName: 'msGetDebtWriteOffDocUploadUrls',
            fileName,
            fileIndex: idx,
          },
          'error'
        );
        return {
          queryKey: `docUpload${idx + 1}`,
          error: {
            message: get(
              ex,
              'response.data.error',
              get(ex, 'message', DEFAULT_ERROR)
            ),
            errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
          },
        };
      }
    });

    const results = await Promise.all(uploadPromises);

    const data = {};
    const errors = [];

    results.forEach(result => {
      if (result.error) {
        errors.push(result.error);
      } else {
        data[result.queryKey] = result.data;
      }
    });

    if (errors.length > 0) {
      return {
        success: false,
        error: errors,
        data,
      };
    }

    return {
      success: true,
      data,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetDebtWriteOffDocUploadUrls',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
        },
      ],
    };
  }
};

export const msGenerateGiroDeduction = async () => {
  try {
    const resp = await financeService().get(FINANCE.generateGiroDeduction);

    return {
      data: {
        generateGiroDeductionArchive: get(resp, 'data.success', true),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGenerateGiroDeduction',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};
export const msGenerateGiroRefund = async () => {
  try {
    const resp = await financeService().get(FINANCE.generateGiroRefund);

    return {
      data: {
        generateGiroRefundArchive: get(resp, 'data.success', true),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGenerateGiroRefund',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msAddUserGiro = async (_, reqData) => {
  try {
    const { giroAccount, paymentType } = reqData;

    const keyMappings = {
      FkChild: 'child_id',
      FkParent: 'parent_id',
      FkRegistrationChild: 'registration_child_id',
      FkEcdaRegistrationChild: 'ecda_registration_child_id',
      FkRegistrationParent: 'registration_parent_id',
    };

    const accountPayload = giroAccount
      ? convertPascalToSnake(giroAccount, keyMappings)
      : {};

    const requestBody = {
      account: accountPayload,
      payment_type: paymentType || '',
    };

    const resp = await financeService().post(...FINANCE.addGiro(requestBody));

    return {
      data: {
        addRegistrationGiro: resp?.data
          ? convertObjToCamelCase(resp.data)
          : null,
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msAddUserGiro',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
        },
      ],
    };
  }
};

export const msApproveCustomSubsidy = async (_, reqData) => {
  try {
    await financeService().post(FINANCE.approveCustomSubsidy, {
      custom_subsidy_id: get(reqData, 'customSubsidyID'),
    });
    return {
      data: {
        approveCustomSubsidy: true,
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msApproveCustomSubsidy',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
        },
      ],
    };
  }
};

export const msGetSubsidyDebitNoteAmount = async (_, reqData) => {
  try {
    const resp = await financeService().post(
      FINANCE.getSubsidyDebitNoteAmount,
      {
        subsidy_arrangement_id: get(reqData, 'subsidyArrangementID'),
      }
    );

    return {
      data: {
        estimateVoidCustomSubsidyDebitNoteAmount: get(resp, 'data.amount', 0),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetSubsidyDebitNoteAmount',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
        },
      ],
    };
  }
};

export const msGetCssProgrammesByFkChild = async (_, reqData) => {
  try {
    const filter = {
      ...(get(reqData, 'filter.year') && {
        year: [{ eq: get(reqData, 'filter.year') }],
      }),
      ...(get(reqData, 'filter.pillarName.length') > 0 && {
        pillar_name: [{ eq: get(reqData, 'filter.pillarName') }],
      }),
    };

    const resp = await financeService().post(
      FINANCE.getCSSProgrammeByChildId,
      {
        child_id: get(reqData, 'IDChild'),
        ...(!isEmpty(filter) && { filter }),
      },
      {
        params: {
          page: get(reqData, 'pagination.page', DEFAULT_PAGE),
          perPage: get(reqData, 'pagination.perPage', DEFAULT_ITEMS_PER_PAGE),
        },
      }
    );

    return {
      data: {
        cssProgrammesByFkChild: {
          data: (get(resp, 'data.data') || []).map(d =>
            convertObjToCamelCase(d)
          ),
          totalCount: get(resp, 'data.totalCount', 0),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetCssProgrammesByFkChild',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msGetCSSPillarNames = async () => {
  try {
    const resp = await financeService().get(FINANCE.getCSSPillarNames);
    return {
      data: {
        getCSSPillarNames: {
          Data: resp?.data?.Data || [],
          TotalCount: resp?.data?.TotalCount || 0,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetCSSPillarNames',
        fileName,
      },
      'error'
    );
    throw ex;
  }
};

export const msAddNewFeeTier = async (_, reqBody) => {
  try {
    const body = {
      items: reqBody?.data?.map(dt => ({
        ...convertObjToSnakeCase(dt, [
          'level_id:fkLevel',
          'program_id:fkProgram',
        ]),
      })),
      ...(reqBody?.effectiveFrom && {
        effectiveFrom: reqBody?.effectiveFrom,
      }),
      ...(reqBody?.IDCentre && {
        idCentre: reqBody?.IDCentre,
      }),
      ...(reqBody?.IDFeeGroup && {
        idFeeGroup: reqBody?.IDFeeGroup,
      }),
      ...(reqBody?.label && {
        label: reqBody?.label,
      }),
    };

    const financeSvc = financeService();
    const resp = await financeSvc.post(...FINANCE.addNewFeeTier(body));

    return {
      success: true,
      data: { newFeeTier: resp?.data && convertObjToCamelCase(resp?.data) },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msAddNewFeeTier',
        fileName,
      },
      'error'
    );
    return { success: false, error };
  }
};

export const msGetAdvancePaymentItemAmountForRegistration = async (
  _,
  reqData
) => {
  try {
    const resp = await financeService().post(
      FINANCE.GET_ADVANCE_PAYMENT_FOR_REGISTRATION,
      {
        registration_child_id: reqData?.registrationChildID,
        registration_id: reqData?.registrationID,
      }
    );

    return {
      data: {
        getAdvancePaymentItemAmountForRegistration: get(resp, 'data[0]', {}),
      },
      success: get(resp, 'data.length', 0) > 0,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAdvancePaymentItemAmountForRegistration',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
        },
      ],
    };
  }
};

export const msGetECDAAdvancePaymentItemAmountForRegistration = async (
  _,
  reqData
) => {
  try {
    const resp = await financeService().post(
      FINANCE.GET_ADVANCE_PAYMENT_FOR_ECDA_REGISTRATION,
      {
        registration_child_id: reqData?.registrationChildID,
        registration_id: reqData?.registrationID,
      }
    );

    return {
      data: {
        getAdvancePaymentItemAmountForEcdaRegistration: get(
          resp,
          'data.data',
          []
        ),
      },
      success: get(resp, 'data.data.length', 0) > 0,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetECDAAdvancePaymentItemAmountForRegistration',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
        },
      ],
    };
  }
};

export const msCancelExportGiroRefund = async (_, reqData) => {
  try {
    if (!get(reqData, 'docId')) {
      return {
        success: false,
      };
    }

    const resp = await financeService().get(
      FINANCE.cancelExportGiroRefund(get(reqData, 'docId'))
    );
    return {
      data: {
        cancelExportGiroRefund: get(resp, 'data.is_success', false),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCancelExportGiroRefund',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
        },
      ],
    };
  }
};

export const msFetchECDAEddaRedirection = async (_, reqData) => {
  try {
    const requestBody = {
      applicant_bank_code: get(reqData, 'applicantBankCode'),
      billing_ref_number: get(reqData, 'billingRefNumber') || null,
      centre_id: get(reqData, 'centreId'),
      child_id: get(reqData, 'childId') || null,
      ecda_registration_child_id: get(reqData, 'ecdaRegistrationChildID'),
      is_ecda_registration: true,
      registration_child_id: get(reqData, 'registrationChildId') || null,
      user_id: get(reqData, 'userID'),
    };

    const resp = await financeService().post(
      ...FINANCE.getEcdaEddaRedirection(requestBody)
    );

    return {
      success: true,
      data: {
        getEddaRedirectionForEcda: get(resp, 'data.url', ''),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchECDAEddaRedirection',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'response.data.error', get(ex, 'message', DEFAULT_ERROR)),
    };
  }
};

export const msFindAllValidDownloadedGiroFiles = async (_, reqData) => {
  try {
    const params = {
      at: reqData?.at,
      page: get(reqData, 'pagination.page', DEFAULT_PAGE),
      ...(get(reqData, 'pagination.perPage') && {
        perPage: get(reqData, 'pagination.perPage'),
      }),
      ...retrieveSortForMS(get(reqData, 'pagination.sort', [])),
    };

    const resp = await financeService().get(
      FINANCE.getAllDownloadedFiles,
      params
    );

    const data = get(resp, 'data.data', []);
    const userIds = uniq(compact(data.map(d => d.fk_user)));

    const users = await getUserByIds(userIds);

    return transformFindAllValidDownloadedGiroFiles(resp, users);
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFindAllValidDownloadedGiroFiles',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetFinanceDocumentPdfPresignedURL = async (_, reqData) => {
  try {
    const body = {
      document_id: reqData?.documentID,
      document_type: reqData?.documentType,
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(
      ...FINANCE.getDocumentUrlPresignedUrl(body)
    );
    return {
      data: {
        getFinanceDocumentPdfPresignedURL: resp?.data?.url || '',
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetFinanceDocumentPdfPresignedURL',
        fileName,
      },
      'error'
    );
  }
};

export const msGetPaymentAccess = async (_, reqData) => {
  try {
    const body = {
      ...(get(reqData, 'authCode') && {
        auth_code: get(reqData, 'authCode'),
      }),
      ...(get(reqData, 'isEcdaRegistration') && {
        is_ecda_registration: get(reqData, 'isEcdaRegistration'),
      }),
      ...(get(reqData, 'paymentType') && {
        payment_type: get(reqData, 'paymentType'),
      }),
      ...(get(reqData, 'sessionToken') && {
        session_token: get(reqData, 'sessionToken'),
      }),
    };
    const financeSvc = financeService();
    const resp = await financeSvc.post(...FINANCE.getPaymentAccess(body));
    return {
      data: {
        getRegistrationPaymentAccess: get(resp, 'data.success', false),
      },
      loading: false,
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetPaymentAccess',
        fileName,
      },
      'error'
    );
  }
};

export const msUpdateUpcomingChildFeeTier = async () => {
  try {
    const res = await Promise.resolve();
    return {
      data: {
        updateUpcomingChildFeeTier: {
          ID: get(res, 'data.id'),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUpdateUpcomingChildFeeTier',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      data: {},
    };
  }
};
